/**
 * BRDGE Journey API Integration Module
 *
 * Generic, reusable service for integrating ANY frontend with BRDGE Journey API.
 * Frontend-agnostic: doesn't assume anything about your data structure or use case.
 *
 * Usage:
 *   1. Configure in your project's config.ts
 *   2. Call createJourney() when you want to start tracking
 *   3. Call recordActivity() whenever you want to log something
 *   4. Module handles all API communication and state management
 *
 * @module journeyApi
 */

import type { FetchOptions } from "ofetch"

// =============================================================================
// Types
// =============================================================================

/**
 * Configuration for Journey API integration
 * Set these in your project's config.ts
 */
export interface JourneyApiConfig {
  /** Enable/disable Journey API integration */
  enabled: boolean

  /** BRDGE Journey API base URL */
  apiUrl: string

  /** Provider API key (get from BRDGE Journey admin) */
  apiKey: string

  /** Project ID in BRDGE Journey */
  projectId: number

  /** Base URL for your application (used for QR code generation) */
  appBaseUrl: string

  /** Error handling strategy */
  errorHandling: "silent" | "warn" | "block"
}

/**
 * Generic visitor data - provide what you have, all fields optional
 */
export interface JourneyVisitorData {
  firstName?: string
  lastName?: string
  email?: string
  company?: string
  /** External reference (e.g., ticket ID, URL param, etc.) */
  externalId?: string
  /** Any additional metadata */
  metadata?: Record<string, any>
}

/**
 * Generic activity data - you decide what to track
 */
export interface ActivityData {
  /** Activity type - you choose the name (e.g., "game_started", "item_purchased") */
  activityType: string

  /** Activity content - any data you want to track */
  content: Record<string, any>

  /** Optional metadata */
  metadata?: Record<string, any>
}

/**
 * Journey API response types
 */
export interface JourneyCreationResponse {
  success: boolean
  journey_id: number
  visitor_id: number
  short_url: string
  qr_code: string
}

export interface ActivityCreationResponse {
  id: number
  journey_id: number
  provider_id: number
  performed_by: number
  activity_type: string
  content: Record<string, any>
  created_at: string
}

// =============================================================================
// Journey API Service Class
// =============================================================================

export class JourneyApiService {
  private config: JourneyApiConfig
  private journeyId: number | null = null
  private visitorId: number | null = null
  private isInitialized = false

  constructor(config: JourneyApiConfig) {
    this.config = config
  }

  /**
   * Check if Journey API is enabled and configured
   */
  isEnabled(): boolean {
    return this.config.enabled && !!this.config.apiKey && !!this.config.apiUrl
  }

  /**
   * Check if journey has been created
   */
  hasJourney(): boolean {
    return this.journeyId !== null && this.visitorId !== null
  }

  /**
   * Get current journey ID (null if not created)
   */
  getJourneyId(): number | null {
    return this.journeyId
  }

  /**
   * Get current visitor ID (null if not created)
   */
  getVisitorId(): number | null {
    return this.visitorId
  }

  /**
   * Create a new journey with visitor data
   *
   * @param visitorData - Visitor information (all fields optional)
   * @returns Journey creation response or null if disabled/failed
   *
   * @example
   * ```typescript
   * const result = await journeyApi.createJourney({
   *   externalId: "CODE123",
   *   firstName: "Anonymous",
   *   metadata: { source: "web_app" }
   * })
   * ```
   */
  async createJourney(
    visitorData: JourneyVisitorData = {}
  ): Promise<JourneyCreationResponse | null> {
    if (!this.isEnabled()) {
      return null
    }

    if (this.hasJourney()) {
      this.log("Journey already exists", "warn")
      return null
    }

    try {
      const requestBody = {
        external_id: visitorData.externalId || this.generateExternalId(),
        visitor: {
          first_name: visitorData.firstName || "Anonymous",
          last_name: visitorData.lastName || "User",
          email: visitorData.email || null,
          company: visitorData.company || null,
          metadata: visitorData.metadata || {}
        },
        base_url: this.config.appBaseUrl
      }

      const response = await this.fetch<JourneyCreationResponse>(
        "/api/v1/journeys",
        {
          method: "POST",
          body: requestBody
        }
      )

      if (response && response.success) {
        this.journeyId = response.journey_id
        this.visitorId = response.visitor_id
        this.isInitialized = true
        this.log(`Journey created: ${response.journey_id}`, "info")
        return response
      }

      throw new Error("Invalid response from Journey API")
    } catch (error) {
      return this.handleError("Failed to create journey", error)
    }
  }

  /**
   * Record an activity in the current journey
   *
   * @param activityType - Name of the activity (you choose)
   * @param content - Activity data (any structure)
   * @param metadata - Optional metadata
   * @returns Activity creation response or null if failed
   *
   * @example
   * ```typescript
   * await journeyApi.recordActivity('game_started', {
   *   difficulty: 'easy',
   *   language: 'en'
   * })
   *
   * await journeyApi.recordActivity('game_completed', {
   *   score: 100,
   *   time_taken: 300
   * })
   * ```
   */
  async recordActivity(
    activityType: string,
    content: Record<string, any> = {},
    metadata: Record<string, any> = {}
  ): Promise<ActivityCreationResponse | null> {
    if (!this.isEnabled() || !this.hasJourney()) {
      return null
    }

    try {
      const requestBody = {
        performed_by: this.visitorId!,
        activity_type: activityType,
        content: content,
        data_location: "LOCAL",
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString()
        }
      }

      this.log(
        `Sending POST to /api/v1/journeys/${this.journeyId}/activities`,
        "info",
        { requestBody }
      )

      const response = await this.fetch<ActivityCreationResponse>(
        `/api/v1/journeys/${this.journeyId}/activities`,
        {
          method: "POST",
          body: requestBody
        }
      )

      if (response) {
        this.log(`✅ Activity recorded successfully: ${activityType}`, "info", {
          activityId: response.id,
          response
        })
        return response
      }

      throw new Error("Invalid response from Journey API")
    } catch (error) {
      return this.handleError(
        `Failed to record activity: ${activityType}`,
        error
      )
    }
  }

  /**
   * Complete the current journey
   *
   * @param metadata - Optional completion metadata
   */
  async completeJourney(metadata: Record<string, any> = {}): Promise<boolean> {
    if (!this.isEnabled() || !this.hasJourney()) {
      return false
    }

    try {
      await this.fetch(`/api/v1/journeys/${this.journeyId}/complete`, {
        method: "POST",
        body: { metadata }
      })

      this.log("Journey completed", "info")
      return true
    } catch (error) {
      this.handleError("Failed to complete journey", error)
      return false
    }
  }

  /**
   * Reset the service state (for testing or re-initialization)
   */
  reset(): void {
    this.journeyId = null
    this.visitorId = null
    this.isInitialized = false
  }

  // ===========================================================================
  // Private Helpers
  // ===========================================================================

  private async fetch<T>(
    endpoint: string,
    options: FetchOptions = {}
  ): Promise<T | null> {
    const url = `${this.config.apiUrl}${endpoint}`

    try {
      const response = await $fetch<T>(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.config.apiKey,
          ...options.headers
        }
      })

      return response
    } catch (error: any) {
      // Log detailed error information
      console.error("[BRDGE Journey] API Error Details:", {
        url,
        status: error.status,
        statusText: error.statusText,
        data: error.data,
        message: error.message
      })
      throw error
    }
  }

  private generateExternalId(): string {
    // Generate a random 8-character ID
    return Math.random().toString(36).substring(2, 10).toUpperCase()
  }

  private log(
    message: string,
    level: "info" | "warn" | "error",
    data?: any
  ): void {
    const prefix = "[BRDGE Journey]"

    if (level === "error") {
      console.error(`${prefix} ${message}`, data || "")
    } else if (level === "warn") {
      console.warn(`${prefix} ${message}`, data || "")
    } else {
      console.log(`${prefix} ${message}`, data || "")
    }
  }

  private handleError(message: string, error: any): null {
    const errorMessage = error instanceof Error ? error.message : String(error)

    if (this.config.errorHandling === "block") {
      throw new Error(`${message}: ${errorMessage}`)
    } else if (this.config.errorHandling === "warn") {
      this.log(`${message}: ${errorMessage}`, "warn")
    } else {
      // silent - only log to console in dev
      if (import.meta.dev) {
        this.log(`${message}: ${errorMessage}`, "error")
      }
    }

    return null
  }
}

// =============================================================================
// Global Instance Management
// =============================================================================

let journeyApiInstance: JourneyApiService | null = null

/**
 * Initialize the Journey API service with configuration
 * Call this once in your app setup/plugin
 */
export function initializeJourneyApi(
  config: JourneyApiConfig
): JourneyApiService {
  journeyApiInstance = new JourneyApiService(config)
  return journeyApiInstance
}

/**
 * Get the global Journey API instance
 * Throws if not initialized
 */
export function getJourneyApi(): JourneyApiService {
  if (!journeyApiInstance) {
    throw new Error(
      "Journey API not initialized. Call initializeJourneyApi() first."
    )
  }
  return journeyApiInstance
}

/**
 * Check if Journey API has been initialized
 */
export function isJourneyApiInitialized(): boolean {
  return journeyApiInstance !== null
}
